<template>
  <div class="orbit-root" aria-hidden="true">
    <svg
      viewBox="0 0 500 500"
      xmlns="http://www.w3.org/2000/svg"
      class="orbit-svg"
    >
      <defs>
        <!-- Hub gradient -->
        <radialGradient id="hub-g" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#5c6bc0" />
          <stop offset="100%" stop-color="#3f51b5" />
        </radialGradient>

        <linearGradient id="g-web" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#00897b" />
          <stop offset="100%" stop-color="#004d40" />
        </linearGradient>

        <linearGradient id="g-mobile" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#e53935" />
          <stop offset="100%" stop-color="#b71c1c" />
        </linearGradient>

        <linearGradient id="g-desktop" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#7c3aed" />
          <stop offset="100%" stop-color="#4c1d95" />
        </linearGradient>

        <linearGradient id="g-ai" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#0284c7" />
          <stop offset="100%" stop-color="#0369a1" />
        </linearGradient>

        <!-- Card drop shadow -->
        <filter id="f-card" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="5" stdDeviation="8" flood-color="rgba(36,54,128,0.14)" />
        </filter>

        <!-- Hub soft glow -->
        <filter id="f-hub" x="-35%" y="-35%" width="170%" height="170%">
          <feGaussianBlur stdDeviation="7" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <!-- ── Background ambient rings ── -->
      <circle cx="250" cy="250" r="220" fill="none" stroke="rgba(63,81,181,0.03)" stroke-width="1.5" />
      <circle cx="250" cy="250" r="185" fill="none" stroke="rgba(63,81,181,0.06)" stroke-width="1" />

      <!-- ── Spinning orbit ring ── -->
      <circle
        cx="250" cy="250" r="162"
        fill="none"
        stroke="rgba(63,81,181,0.20)"
        stroke-width="1.5"
        stroke-dasharray="8 8"
        class="orbit-ring"
      />

      <!-- ── Connector lines (hub edge → node edge, flowing dash) ── -->
      <line x1="250" y1="195" x2="250" y2="102" stroke="#00897b"
        stroke-width="1.6" stroke-dasharray="4 5" stroke-linecap="round" opacity="0.65" class="conn c1" />
      <line x1="298" y1="250" x2="385" y2="250" stroke="#e53935"
        stroke-width="1.6" stroke-dasharray="4 5" stroke-linecap="round" opacity="0.65" class="conn c2" />
      <line x1="250" y1="305" x2="250" y2="395" stroke="#7c3aed"
        stroke-width="1.6" stroke-dasharray="4 5" stroke-linecap="round" opacity="0.65" class="conn c3" />
      <line x1="202" y1="250" x2="115" y2="250" stroke="#0284c7"
        stroke-width="1.6" stroke-dasharray="4 5" stroke-linecap="round" opacity="0.65" class="conn c4" />

      <!-- ── Hub glow behind ── -->
      <circle cx="250" cy="250" r="76" fill="rgba(63,81,181,0.08)" class="hub-glow" />
      <circle cx="250" cy="250" r="64" fill="rgba(63,81,181,0.05)" />

      <!-- ── Hub ── -->
      <circle cx="250" cy="250" r="52" fill="url(#hub-g)" filter="url(#f-hub)" class="hub-beat" />
      <circle cx="250" cy="250" r="58" fill="none" stroke="rgba(92,107,192,0.35)" stroke-width="1.5" />
      <!-- Hub icon: Core architecture -->
      <path d="M236 240 L250 232 L264 240 L250 248 Z" fill="none" stroke="white" stroke-width="2" stroke-linejoin="round" />
      <path d="M236 248 L250 256 L264 248" fill="none" stroke="white" stroke-width="2" stroke-linejoin="round" />
      <path d="M236 256 L250 264 L264 256" fill="none" stroke="white" stroke-width="2" stroke-linejoin="round" />
      <circle cx="250" cy="222" r="3" fill="#4caf50" class="dot" />

      <!-- ── Node 1 — Web & SaaS (Top) ── -->
      <g class="app-node n1">
        <rect x="195" y="52" width="110" height="52" rx="14"
          fill="white" stroke="rgba(0,137,123,0.22)" stroke-width="1.2" filter="url(#f-card)" />
        <rect x="201" y="52" width="98" height="4" rx="2" fill="#00897b" />
        <circle cx="218" cy="78" r="10" fill="rgba(0,137,123,0.10)" />
        <!-- Globe/Web Icon -->
        <circle cx="218" cy="78" r="6" fill="none" stroke="#00897b" stroke-width="1.3" />
        <line x1="212" y1="78" x2="224" y2="78" stroke="#00897b" stroke-width="1" />
        <text x="264" y="74" text-anchor="middle" font-size="9" font-weight="700" fill="#0f172a">{{ isArabic ? 'منصات الويب' : 'Web & SaaS' }}</text>
        <text x="264" y="88" text-anchor="middle" font-size="7" font-weight="500" fill="#64748b">Cloud & Web Apps</text>
        <circle cx="295" cy="58" r="4" fill="#10b981" class="dot" />
      </g>

      <!-- ── Node 2 — Mobile Apps (Right) ── -->
      <g class="app-node n2">
        <rect x="382" y="224" width="108" height="52" rx="14"
          fill="white" stroke="rgba(229,57,53,0.22)" stroke-width="1.2" filter="url(#f-card)" />
        <rect x="388" y="224" width="96" height="4" rx="2" fill="#e53935" />
        <circle cx="404" cy="250" r="10" fill="rgba(229,57,53,0.10)" />
        <!-- Mobile Phone Icon -->
        <rect x="399" y="243" width="10" height="15" rx="2" fill="none" stroke="#e53935" stroke-width="1.3" />
        <circle cx="404" cy="255" r="1" fill="#e53935" />
        <text x="448" y="246" text-anchor="middle" font-size="9" font-weight="700" fill="#0f172a">{{ isArabic ? 'تطبيقات الجوال' : 'Mobile Apps' }}</text>
        <text x="448" y="260" text-anchor="middle" font-size="7" font-weight="500" fill="#64748b">iOS & Android</text>
        <circle cx="480" cy="230" r="4" fill="#10b981" class="dot" style="animation-delay: 0.85s" />
      </g>

      <!-- ── Node 3 — Enterprise & Desktop (Bottom) ── -->
      <g class="app-node n3">
        <rect x="195" y="394" width="110" height="52" rx="14"
          fill="white" stroke="rgba(124,58,237,0.22)" stroke-width="1.2" filter="url(#f-card)" />
        <rect x="201" y="394" width="98" height="4" rx="2" fill="#7c3aed" />
        <circle cx="218" cy="420" r="10" fill="rgba(124,58,237,0.10)" />
        <!-- Desktop Monitor Icon -->
        <rect x="211" y="413" width="14" height="10" rx="1.5" fill="none" stroke="#7c3aed" stroke-width="1.3" />
        <line x1="215" y1="426" x2="221" y2="426" stroke="#7c3aed" stroke-width="1.3" stroke-linecap="round" />
        <text x="264" y="416" text-anchor="middle" font-size="9" font-weight="700" fill="#0f172a">{{ isArabic ? 'أنظمة سطح المكتب' : 'Enterprise Apps' }}</text>
        <text x="264" y="430" text-anchor="middle" font-size="7" font-weight="500" fill="#64748b">Windows, Mac, Linux</text>
        <circle cx="295" cy="400" r="4" fill="#10b981" class="dot" style="animation-delay: 1.7s" />
      </g>

      <!-- ── Node 4 — AI & Custom Integrations (Left) ── -->
      <g class="app-node n4">
        <rect x="10" y="224" width="110" height="52" rx="14"
          fill="white" stroke="rgba(2,132,199,0.22)" stroke-width="1.2" filter="url(#f-card)" />
        <rect x="16" y="224" width="98" height="4" rx="2" fill="#0284c7" />
        <circle cx="32" cy="250" r="10" fill="rgba(2,132,199,0.10)" />
        <!-- AI / Chip Icon -->
        <rect x="26" y="244" width="12" height="12" rx="2" fill="none" stroke="#0284c7" stroke-width="1.3" />
        <circle cx="32" cy="250" r="2.5" fill="#0284c7" />
        <text x="76" y="246" text-anchor="middle" font-size="8.5" font-weight="700" fill="#0f172a">{{ isArabic ? 'الذكاء والربط' : 'AI & APIs' }}</text>
        <text x="76" y="260" text-anchor="middle" font-size="7" font-weight="500" fill="#64748b">Integrations & AI</text>
        <circle cx="110" cy="230" r="4" fill="#10b981" class="dot" style="animation-delay: 2.3s" />
      </g>

      <!-- ── Floating tech pills ── -->
      <g class="chip cp1">
        <rect x="330" y="92" width="60" height="24" rx="12"
          fill="rgba(0,137,123,0.10)" stroke="rgba(0,137,123,0.30)" stroke-width="1" />
        <text x="360" y="107" text-anchor="middle" font-size="7.5" fill="#00897b" font-weight="700">Vue / React</text>
      </g>
      <g class="chip cp2">
        <rect x="340" y="340" width="60" height="24" rx="12"
          fill="rgba(229,57,53,0.10)" stroke="rgba(229,57,53,0.30)" stroke-width="1" />
        <text x="370" y="355" text-anchor="middle" font-size="7.5" fill="#e53935" font-weight="700">Flutter / iOS</text>
      </g>
      <g class="chip cp3">
        <rect x="100" y="340" width="60" height="24" rx="12"
          fill="rgba(124,58,237,0.10)" stroke="rgba(124,58,237,0.30)" stroke-width="1" />
        <text x="130" y="355" text-anchor="middle" font-size="7.5" fill="#7c3aed" font-weight="700">Node / Nest</text>
      </g>
      <g class="chip cp4">
        <rect x="100" y="92" width="60" height="24" rx="12"
          fill="rgba(2,132,199,0.10)" stroke="rgba(2,132,199,0.30)" stroke-width="1" />
        <text x="130" y="107" text-anchor="middle" font-size="7.5" fill="#0284c7" font-weight="700">AI / Cloud</text>
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'EcosystemOrbit',
  computed: {
    isArabic() {
      return this.$i18n.locale === 'ar';
    }
  }
};
</script>

<style scoped lang="scss">
.orbit-root {
  position: relative;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    inset: 8% 12%;
    border-radius: 50%;
    background: radial-gradient(ellipse at center, rgba(63,81,181,0.14) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .orbit-svg { position: relative; z-index: 1; }

  @media (max-width: 1264px) { max-width: 420px; }
  @media (max-width: 960px)  { max-width: 360px; margin-top: 24px; }
  @media (max-width: 600px)  { max-width: 290px; margin-top: 16px; }
}

.orbit-svg {
  width: 100%;
  height: auto;
  overflow: visible;

  ::v-deep text {
    font-family: 'Almarai', 'Segoe UI', system-ui, sans-serif;
  }
}

// ─── Keyframes ────────────────────────────────────────────────────────────────
@keyframes ring-spin {
  to { transform: rotate(360deg); }
}

@keyframes hub-beat {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.05); }
}

@keyframes node-float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-6px); }
}

@keyframes dash-flow {
  to { stroke-dashoffset: -18; }
}

@keyframes chip-pulse {
  0%, 100% { opacity: 0.55; transform: scale(0.95); }
  50%       { opacity: 1;    transform: scale(1.02); }
}

@keyframes dot-blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.2; }
}

// ─── Animated elements ────────────────────────────────────────────────────────
.orbit-ring {
  transform-box: fill-box;
  transform-origin: center;
  animation: ring-spin 35s linear infinite;
}

.hub-beat {
  transform-box: fill-box;
  transform-origin: center;
  animation: hub-beat 4s ease-in-out infinite;
}

.hub-glow {
  transform-box: fill-box;
  transform-origin: center;
  animation: hub-beat 4s ease-in-out infinite 0.3s;
}

.app-node {
  transform-box: fill-box;
  transform-origin: center;
}
.n1 { animation: node-float 6s ease-in-out infinite; }
.n2 { animation: node-float 7s ease-in-out infinite 1s; }
.n3 { animation: node-float 5.8s ease-in-out infinite 2s; }
.n4 { animation: node-float 6.5s ease-in-out infinite 3s; }

.conn {
  stroke-dashoffset: 0;
  animation: dash-flow 2s linear infinite;
}
.c2 { animation-delay: 0.5s; }
.c3 { animation-delay: 1.0s; }
.c4 { animation-delay: 1.5s; }

.chip {
  transform-box: fill-box;
  transform-origin: center;
  animation: chip-pulse 4.5s ease-in-out infinite;
}
.cp2 { animation-delay: 1.2s; }
.cp3 { animation-delay: 2.4s; }
.cp4 { animation-delay: 3.6s; }

.dot {
  animation: dot-blink 2.5s ease-in-out infinite;
}
</style>

